import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Box, IconButton, Tooltip, Button, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DirectionsIcon from '@material-ui/icons/Directions';
import SearchIcon from '@material-ui/icons/Search';
import NearMeIcon from '@material-ui/icons/NearMe';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MapIcon from '@material-ui/icons/Map';
import InputBase from '@material-ui/core/InputBase';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Header from './Header';
import SidebarPanel from '../Sidebar/SidebarPanel';
import MapView from '../Map/MapView';
import MapViewGoogle from '../Map/MapViewGoogle';
import RouteDetails from '../Details/RouteDetails';
import ReportsModal from '../Reports/ReportsModal';
import PhonePreview from '../MobilePreview/PhonePreview';
import DashboardView from '../Dashboard/DashboardView';
import FuelOpsDashboard from '../FuelOps/FuelOpsDashboard';
import CasaisDashboard from '../Casais/CasaisDashboard';
import FleetView from '../Fleet/FleetView';
import TachoDriversView from '../Fleet/TachoDriversView';
import OrdersView from '../Orders/OrdersView';
import VRPView from '../VRP/VRPView';
import ReportsView from '../Reports/ReportsView';
import MaintenanceHomeView from '../Manage/MaintenanceHomeView';
import OrgAdminView from '../Manage/OrgAdminView';
import FinalCustomersView from '../Manage/FinalCustomersView';
import CustomerSitesView from '../Manage/CustomerSitesView';
import SiteEquipmentsView from '../Manage/SiteEquipmentsView';
import ObrasView from '../Manage/ObrasView';
import MotoristasView from '../Manage/MotoristasView';
import ViaturasView from '../Manage/ViaturasView';
import TiposTransporteView from '../Manage/TiposTransporteView';
import SLAContractsView from '../Manage/SLAContractsView';
import StockItemsView from '../Manage/StockItemsView';
import StockByVehicleView from '../Manage/StockByVehicleView';
import AccountingCodesView from '../Manage/AccountingCodesView';
import HolidaysAngolaView from '../Manage/HolidaysAngolaView';
import CostsSlaDashboardView from '../Manage/CostsSlaDashboardView';
import RoutesView from '../Routes/RoutesView';
import RoutesBrowserDrawer from '../Routes/RoutesBrowserDrawer';
import ManageDialog from '../common/ManageDialog';
import ManageFavoritesDialog from '../common/ManageFavoritesDialog';
import ModuleGate from '../common/ModuleGate';
import { selectUI, setDetailsPanelOpen, setShowDetails, setSidebarVisible, setSidebarCollapsed as setSidebarCollapsedAction, setActiveView, navigateTo, clearPreviousView, setSidebarTab, setResetMapView, setDisplayedSavedRoutes, setNeedsRecalculation } from '../../store/slices/uiSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectSavedRoutes, selectCurrentRoute, fetchRouteDetail, clearRoute, saveRouteToAPI, selectActualTrail, setWaypoints, setDestination } from '../../store/slices/routesSlice';
import { analyzeTrail } from '../../utils/trailGeom';
import { geocodeSearchMulti } from '../../utils/mapboxApi';
import { fetchVehicles, loadMockVehicles, setVehiclesFromWS, updateSimulatedVehicle, clearSimulatedVehicle } from '../../store/slices/vehiclesSlice';
import { fetchLocations } from '../../store/slices/locationsSlice';
import { fetchDrivers, loadMockDrivers } from '../../store/slices/driversSlice';
import { fetchSavedRoutes } from '../../store/slices/routesSlice';
import { AuthContext } from '../../App';
import HelpCenter from '../Help/HelpCenter';
import { isMoveExpressTenant } from '../../utils/manageHelpers';
import { connectWS, disconnectWS } from '../../utils/websocket';
import { setAlerts, addAlert, addToast, setWSConnected } from '../../store/slices/notificationsSlice';
import ToastContainer from '../common/ToastContainer';

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    background: '#e8edf3',
  },
  // Map — fills entire screen behind header
  mapContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  // Floating sidebar (left) — below header
  floatingSidebar: {
    position: 'absolute',
    top: 62,
    left: 12,
    zIndex: 500,
    width: 400,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(20px)',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    overflow: 'visible',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
    transform: 'translateX(0)',
    opacity: 1,
  },
  floatingSidebarHidden: {
    transform: 'translateX(-110%)',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Floating details panel (right)
  floatingDetails: {
    position: 'absolute',
    top: 62,
    right: 12,
    zIndex: 500,
    width: 380,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(20px)',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    overflow: 'visible',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
    transform: 'translateX(0)',
    opacity: 1,
  },
  floatingDetailsHidden: {
    transform: 'translateX(110%)',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Edge toggle on sidebar border
  sidebarEdgeToggle: {
    position: 'absolute',
    top: '50%',
    right: -14,
    transform: 'translateY(-50%)',
    zIndex: 510,
    width: 14,
    height: 44,
    minWidth: 0,
    padding: 0,
    borderRadius: '0 8px 8px 0',
    background: '#ffffff',
    border: '1px solid rgba(0,0,0,0.10)',
    borderLeft: 'none',
    color: '#3b82f6',
    boxShadow: '2px 0 8px rgba(0,0,0,0.10)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s, color 0.2s',
    '&:hover': {
      background: '#3b82f6',
      color: '#fff',
    },
  },
  // Close button for floating panels
  panelClose: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    width: 28,
    height: 28,
    background: 'rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 8,
    color: '#64748b',
    padding: 0,
    transition: 'all 0.15s',
    '&:hover': {
      background: '#ef4444',
      color: '#fff',
      borderColor: '#ef4444',
    },
  },
  // Search bar (compact, visible when sidebar hidden)
  searchBar: {
    position: 'absolute',
    top: 62,
    left: 12,
    zIndex: 400,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: 320,
    height: 42,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
    padding: '0 6px 0 14px',
    cursor: 'pointer',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease, box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.06)',
    },
  },
  searchBarHidden: {
    transform: 'translateX(-110%)',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Fullscreen views (dashboard, fleet, orders) — cover map
  fullView: {
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 600,
    background: '#f8fafc',
    overflow: 'auto',
  },
  // Responsive
  '@media (max-width: 900px)': {
    floatingSidebar: {
      width: 'calc(100vw - 24px)',
      left: 12,
      right: 12,
    },
    floatingDetails: {
      width: 'calc(100vw - 24px)',
      left: 12,
      right: 12,
    },
    searchBar: {
      width: 'calc(100vw - 24px)',
    },
  },
}));

// Small inline arrow connector used between cards in the "Troços da rota
// conjunta" timeline. Renders an arrow with the segment's distance/time
// stacked above the arrowhead.
function ConnectorArrow({ km, dur }) {
  return (
    <Box style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '0 4px', minWidth: 56, alignSelf: 'stretch',
    }}>
      <Typography style={{
        fontSize: '0.5rem', fontWeight: 800, color: '#475569', lineHeight: 1.1,
        whiteSpace: 'nowrap',
      }}>{km || '—'}</Typography>
      <Typography style={{
        fontSize: '0.48rem', fontWeight: 600, color: '#64748b', lineHeight: 1.1, marginBottom: 1,
        whiteSpace: 'nowrap',
      }}>{dur || ''}</Typography>
      <Box style={{
        display: 'flex', alignItems: 'center', width: '100%',
      }}>
        <Box style={{ flex: 1, height: 2, background: '#cbd5e1', borderRadius: 1 }} />
        <Box style={{
          width: 0, height: 0,
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderLeft: '6px solid #cbd5e1',
          marginLeft: -1,
        }} />
      </Box>
    </Box>
  );
}

function AppLayout({ authMode }) {
  const dispatch = useDispatch();
  const { token, clientSettings, clientExternalId } = useContext(AuthContext);
  // Dashboard consciente da variante do Perfil (Fase C): quando o uiVariant
  // efetivo é 'fuel' (Move Express), o menu Dashboard mostra o dashboard de
  // combustível em vez do genérico. Config-driven, sem hardcoding de cliente.
  const isFuelVariant = isMoveExpressTenant(clientExternalId, clientSettings);
  // Variante mercadoria (Casais): dashboard próprio "Análise Global" (migração CasaisHub).
  const isMercadoriaVariant = clientSettings?.uiVariant === 'mercadoria';

  // Derive default route currency from tenant timezone (Angola → AOA, Portugal → EUR, etc.)
  // Runs once on mount so the fuel price and cost labels show the right units.
  React.useEffect(() => {
    const tz = clientSettings?.timezone;
    if (!tz) return;
    const tzToCurrency = {
      'Africa/Luanda': 'AOA',
      'Africa/Maputo': 'EUR', // Mozambique uses MZN — keep EUR for now
      'Africa/Bissau': 'EUR',
      'Africa/Praia': 'EUR',
      'Africa/Sao_Tome': 'EUR',
      'Europe/Lisbon': 'EUR',
      'Atlantic/Madeira': 'EUR',
      'Atlantic/Azores': 'EUR',
      'Europe/Madrid': 'EUR',
      'Europe/Paris': 'EUR',
      'Europe/Brussels': 'EUR',
    };
    const targetCurrency = tzToCurrency[tz];
    if (!targetCurrency) return;
    const { setRouteOptions } = require('../../store/slices/routesSlice');
    dispatch(setRouteOptions({ currency: targetCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSettings?.timezone]);

  useEffect(() => {
    if (token) {
      dispatch(fetchVehicles(token));
      dispatch(fetchDrivers(token));
      dispatch(fetchSavedRoutes(token));
      dispatch(fetchLocations(token));
    } else if (authMode === 'api') {
      dispatch(loadMockVehicles());
      dispatch(loadMockDrivers());
    } else {
      dispatch(loadMockVehicles());
      dispatch(loadMockDrivers());
    }
  }, [dispatch, authMode, token]);

  // Auto-sync das geofences do EcooGPS → favoritos (1× por sessão do browser).
  // Só para tenants com a flag `geofenceFavorites`. O endpoint é idempotente
  // (dedup por id/nome), por isso correr a cada sessão é seguro — só acrescenta
  // as novas. Silencioso; erros são ignorados.
  useEffect(() => {
    if (!token || !clientSettings?.geofenceFavorites) return;
    try { if (sessionStorage.getItem('geofencesSynced') === '1') return; } catch { /* sessionStorage indisponível */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/planner-api/locations/import-geofences', {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
        try { sessionStorage.setItem('geofencesSynced', '1'); } catch { /* ignore */ }
        if (!cancelled && r.ok) {
          const d = await r.json();
          if (d.imported > 0) dispatch(fetchLocations(token));
        }
      } catch { /* silencioso — o botão manual fica sempre disponível */ }
    })();
    return () => { cancelled = true; };
  }, [token, clientSettings?.geofenceFavorites, dispatch]);

  // Refresh saved routes when window regains focus
  useEffect(() => {
    function onFocus() {
      if (token) dispatch(fetchSavedRoutes(token));
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch, token]);

  // WebSocket connection — replaces polling for vehicle positions and alerts
  const wsConnectedRef = useRef(false);
  useEffect(() => {
    if (!token) return;

    const socket = connectWS(token, {
      onVehicles: (vehicles) => {
        if (vehicles && Array.isArray(vehicles)) {
          dispatch(setVehiclesFromWS(vehicles));
        }
      },
      onAlerts: (data) => {
        if (data) dispatch(addAlert({ ...data, source: 'realtime', icon: '🔔', time: new Date().toISOString() }));
      },
      onRouteStatus: (data) => {
        if (data) {
          // Sub-fase "regresso a casa": a última OT foi entregue e o motorista
          // está a voltar ao ponto final (casa/armazém). A rota mantém-se
          // in_progress — avisamos o operador para não ficar "às escuras"
          // quando todas as OTs já aparecem como concluídas.
          if (data.phase === 'returning') {
            dispatch(addToast({
              type: 'route', severity: 'info',
              message: `🏠 Rota "${data.routeName || data.routeId}" — motorista em regresso a casa`,
              detail: 'Entregas concluídas. Acompanha a posição no Live / no ecrã de Ordens.',
            }));
            dispatch(addAlert({ ...data, source: 'route', icon: '🏠', message: `${data.routeName || 'Rota'}: motorista em regresso`, time: data.timestamp }));
            dispatch(fetchSavedRoutes(token));
            window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'route:returning', data } }));
            return;
          }
          const statusLabels = { in_progress: 'Em Trânsito', completed: 'Concluída', cancelled: 'Cancelada', planned: 'Planeada' };
          dispatch(addToast({
            type: 'route',
            severity: data.status === 'completed' ? 'success' : data.status === 'cancelled' ? 'warning' : 'info',
            message: `Rota "${data.routeName || data.routeId}" → ${statusLabels[data.status] || data.status}`,
            detail: data.driverName ? `Motorista: ${data.driverName}` : '',
          }));
          dispatch(addAlert({ ...data, source: 'route', icon: '🚛', message: `${data.routeName || 'Rota'}: ${statusLabels[data.status] || data.status}`, time: data.timestamp }));
          // Refresh saved routes list
          dispatch(fetchSavedRoutes(token));
          // Notify the Orders view to refresh — route status changes typically affect the linked OT
          window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'route:status', data } }));
        }
      },
      onOrderDelivered: (data) => {
        // Driver confirmed an OT in the mobile app. We bump a refresh
        // counter so OrdersView/LiveRoutePanel reload, and show a small
        // success toast so the dispatcher sees the activity even if not
        // looking at the orders screen. Saved as a window event so any
        // mounted view can listen without changing Redux shape.
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'order:delivered', data } }));
        dispatch(addToast({
          type: 'success',
          severity: 'success',
          message: data.orderNumber
            ? `✓ Entregue: ${data.orderNumber}`
            : '✓ OT entregue pelo motorista',
        }));
      },
      onRouteDeviation: (data) => {
        // Driver is off the planned line — surface a toast so even if the
        // operator isn't on the live monitor they know something's up.
        // Throttled at the source (the monitor only POSTs once per 60s
        // while the deviation is active), so re-firing here is safe.
        dispatch(addToast({
          type: 'warning',
          severity: 'warning',
          message: data.routeId
            ? `⚠ Rota #${data.routeId} fora do trajecto — ${Math.round(data.distanceM || 0)}m`
            : '⚠ Motorista fora do trajecto planeado',
        }));
      },
      onFuelMilestone: (data) => {
        // Marco de OT de combustível (Fase C). Toast para o operador +
        // refresh das OT (o painel/cronologia reflecte o novo marco).
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'fuel:milestone', data } }));
        dispatch(addToast({
          type: 'info',
          severity: 'info',
          message: `⛽ ${data.message || (lang === 'pt' ? 'Atualização de serviço' : 'Service update')}`,
        }));
      },
      onSpeedAlarmSevere: (data) => {
        // Excesso de velocidade GRAVE em tempo real (módulo speed_alarms).
        const evs = (data && data.events) || [];
        const f = evs[0];
        const extra = (data.count || evs.length) > 1 ? ` · +${(data.count || evs.length) - 1}` : '';
        const msg = f
          ? `🚨 ${lang === 'pt' ? 'Excesso grave' : 'Severe speeding'} · ${f.vehiclePlate || '—'} · ${Math.round(f.speedKmh || 0)} km/h (+${Math.round(f.overByKmh || 0)})${extra}`
          : `🚨 ${data.count || evs.length} ${lang === 'pt' ? 'alarme(s) grave(s)' : 'severe alarm(s)'}`;
        dispatch(addToast({ type: 'warning', severity: 'warning', message: msg }));
        // Refresca a lista de alarmes se o operador a tiver aberta.
        window.dispatchEvent(new CustomEvent('speedAlarmsNeedRefresh', { detail: data }));
      },
      onOrderArrived: (data) => {
        // Driver reached the service location (manual button or geofence).
        // The OT status itself doesn't change (still in_transit on the DB),
        // but the listing flips its chip to "No local" and the timeline
        // shows the new event — so we just nudge a refresh.
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'order:arrived', data } }));
        dispatch(addToast({
          type: 'info',
          severity: 'info',
          message: data.orderNumber
            ? `📍 Chegou ao local: ${data.orderNumber}${data.source === 'geofence' ? ' (auto)' : ''}`
            : '📍 Motorista chegou ao local',
        }));
      },
      onOrderServiceStarted: (data) => {
        // Motorista carregou em "Iniciar serviço" — a OT muda do
        // sub-estado "No local" (à espera) para "Em serviço" (a executar).
        // É o sinal que separa o tempo de espera do tempo facturável.
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'order:service_started', data } }));
        dispatch(addToast({
          type: 'info',
          severity: 'info',
          message: data.orderNumber
            ? `🛠 Em serviço: ${data.orderNumber}`
            : '🛠 Motorista iniciou o serviço',
        }));
      },
      onOrderStatus: (data) => {
        // Generic OT status change — nudge a refresh so the table reflects
        // it without manual reload.
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'order:status', data } }));
      },
      onRouteAborted: (data) => {
        // Driver hit the emergency button. Show a loud red toast + a sticky
        // alert in the bell so the dispatcher can re-assign the orphaned OTs.
        const typeLabel = ({
          breakdown: 'Avaria mecânica',
          accident: 'Acidente',
          illness: 'Doença',
          other: 'Incidente',
        })[data.type] || 'Incidente';
        dispatch(addToast({
          type: 'route',
          severity: 'error',
          message: `🚨 ${typeLabel} — ${data.routeName || 'Rota'}`,
          detail: `${data.reason || ''}${data.affectedOTIds?.length ? ` · ${data.affectedOTIds.length} OT(s) libertadas` : ''}`,
          duration: 12000,
        }));
        dispatch(addAlert({
          source: 'incident',
          severity: 'high',
          icon: '🚨',
          message: `${typeLabel} — ${data.routeName || `Rota #${data.routeId}`}`,
          detail: data.reason,
          routeId: data.routeId,
          time: data.timestamp || new Date().toISOString(),
        }));
        // Force orders + routes refresh — released OTs need to show in the
        // dispatcher list with status='pending' immediately.
        window.dispatchEvent(new CustomEvent('ordersNeedRefresh', { detail: { reason: 'route:aborted', data } }));
        dispatch(fetchSavedRoutes(token));
      },
      onRouteIncident: (data) => {
        // Monitor server-side detectou um novo incidente crítico (acidente,
        // via cortada, etc.) sobre uma rota em execução. data = {
        //   routeId, routeName, incidents: [{ id, description, criticality,
        //     roadClosed, coordinates, endTime, _routeDistanceM }]
        // }
        const incs = Array.isArray(data?.incidents) ? data.incidents : [];
        if (incs.length === 0) return;
        const closed = incs.some(i => i.roadClosed);
        const head = closed ? '🚨 Via cortada' : '⚠ Incidente na rota';
        const summary = incs[0].description || (closed ? 'estrada fechada' : 'incidente crítico');
        const more = incs.length > 1 ? ` (+${incs.length - 1})` : '';
        dispatch(addToast({
          type: 'route',
          severity: closed ? 'error' : 'warning',
          message: `${head} — ${data.routeName || `Rota #${data.routeId}`}`,
          detail: `${summary}${more} · a ~${incs[0]._routeDistanceM} m do trajecto`,
          duration: 12000,
        }));
        dispatch(addAlert({
          source: 'incident',
          severity: closed ? 'high' : 'medium',
          icon: closed ? '🚨' : '⚠',
          message: `${head} — ${data.routeName || `Rota #${data.routeId}`}`,
          detail: summary,
          routeId: data.routeId,
          time: new Date().toISOString(),
        }));
      },
      onGeofenceAlert: (data) => {
        if (data?.alerts) {
          data.alerts.forEach(a => {
            dispatch(addToast({
              type: 'geofence',
              severity: 'warning',
              message: `${a.alertType === 'entry' ? 'Entrou em' : 'Saiu de'} ${a.geofenceName || 'geofence'}`,
            }));
            dispatch(addAlert({ ...a, source: 'geofence', icon: '📍', message: `${a.alertType === 'entry' ? 'Entrou em' : 'Saiu de'} ${a.geofenceName}`, time: new Date().toISOString() }));
          });
        }
      },
      onConnect: (id) => {
        wsConnectedRef.current = true;
        dispatch(setWSConnected({ connected: true, id }));
      },
      onDisconnect: () => {
        wsConnectedRef.current = false;
        dispatch(setWSConnected({ connected: false }));
      },
      onError: () => {
        wsConnectedRef.current = false;
        dispatch(setWSConnected({ connected: false }));
      },
      onVehicleSimulated: (data) => {
        if (data?.position) {
          dispatch(updateSimulatedVehicle(data));
          // Follow vehicle on map
          window.dispatchEvent(new CustomEvent('simulationPosition', {
            detail: { lat: data.position.latitude, lng: data.position.longitude, atStop: data.atStop }
          }));
          // Toast at key milestones
          if (data.progress === 25 || data.progress === 50 || data.progress === 75) {
            dispatch(addToast({
              type: 'simulation', severity: 'info',
              message: `${data.plate} — ${data.progress}% da rota`,
              detail: `${Math.round(data.position.speed)} km/h`,
            }));
          }
        }
      },
      onSimulationStop: (data) => {
        // Service stop (an OT delivery) gets a richer "Serviço X às HH:MM"
        // alert; tachograph rest stops keep the legacy "Paragem" toast.
        const eta = data.eta ? new Date(data.eta) : null;
        const etaStr = eta && !isNaN(eta) ? eta.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : null;
        if (data.kind === 'service') {
          dispatch(addToast({
            type: 'simulation', severity: 'info',
            message: data.orderNumber
              ? `🛎 Serviço ${data.orderNumber}${etaStr ? ` às ${etaStr}` : ''}`
              : `🛎 ${data.label}${etaStr ? ` às ${etaStr}` : ''}`,
            detail: `${data.vehiclePlate} chegou ao cliente${data.duration ? ` · ${data.duration} min de serviço` : ''}`,
          }));
        } else {
          dispatch(addToast({
            type: 'simulation', severity: 'warning',
            message: `${data.vehiclePlate} — Paragem: ${data.label}${etaStr ? ` às ${etaStr}` : ''}`,
            detail: `Km ${data.km} · Pausa ${data.duration}min`,
          }));
        }
      },
      onSimulationComplete: (data) => {
        dispatch(addToast({
          type: 'simulation', severity: 'success',
          message: `Simulação concluída: ${data.vehiclePlate}`,
        }));
        if (data.vehiclePlate) dispatch(clearSimulatedVehicle(data.vehiclePlate));
      },
      onSimulationReplanned: (data) => {
        // Mid-route re-plan triggered on the backend. The sim was restarted
        // with the new geometry, resuming near the old position.
        dispatch(addToast({
          type: 'simulation', severity: 'info',
          message: `🔄 Simulação reiniciada com nova rota (rota ${data.routeId})`,
        }));
      },
    });

    // Fallback: poll every 30s ONLY if WS is disconnected
    const fallbackInterval = setInterval(() => {
      if (!wsConnectedRef.current) {
        dispatch(fetchVehicles(token));
      }
    }, 30000);

    return () => {
      clearInterval(fallbackInterval);
      disconnectWS();
    };
  }, [dispatch, token]);

  const classes = useStyles();
  const { activeView, previousView, showDetails, detailsPanelOpen, showReports, showMobilePreview, lang, sidebarVisible, sidebarCollapsed, mapProvider, autoSaveReturn } = useSelector(selectUI);
  const vehicles = useSelector(selectVehicles);
  const savedRoutes = useSelector(selectSavedRoutes);
  const currentRoute = useSelector(selectCurrentRoute);
  const monitoringActualTrail = useSelector(selectActualTrail);
  const [savingAndDispatching, setSavingAndDispatching] = useState(false);
  const [cameFromOT, setCameFromOT] = useState(false);
  const [monitoringVehicle, setMonitoringVehicle] = useState(null);
  const setSidebarCollapsed = (v) => dispatch(setSidebarCollapsedAction(v));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchPin, setSearchPin] = useState(null); // { lat, lng, name }
  const searchTimerRef = React.useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(410);
  const [monitoringBarHeight, setMonitoringBarHeight] = useState(220); // px, resizable
  const [monitoringBarCollapsed, setMonitoringBarCollapsed] = useState(false);
  // Ao iniciar o REPLAY do percurso, minimiza o card inferior (paragens/OT)
  // para deixar o mapa limpo. O botão do replay (MapToolbar) dispara o evento.
  useEffect(() => {
    const onReplay = () => setMonitoringBarCollapsed(true);
    window.addEventListener('openRouteReplay', onReplay);
    return () => window.removeEventListener('openRouteReplay', onReplay);
  }, []);
  const monitoringBarDragRef = React.useRef(null);
  // Track which OT leg is currently highlighted on the map so the monitoring bar
  // can mark that card as active. Kept in sync with the highlightOTLeg event.
  const [activeOTLegId, setActiveOTLegId] = useState(null);
  // Mirror window.__monitoringRouteLegs into React state so the bottom
  // panel re-renders whenever loadOTOnMap dispatches a fresh leg list
  // (e.g. after the operator edited the route and clicked "Ver rota no
  // mapa" again — without this the panel stayed pinned to the stale
  // legs from the previous view).
  const [monitoringLegs, setMonitoringLegs] = useState(() =>
    Array.isArray(window.__monitoringRouteLegs) ? window.__monitoringRouteLegs : []);
  useEffect(() => {
    function onHighlight(e) {
      setActiveOTLegId(e.detail?.orderId || null);
      // The Orders screen ships the freshest route legs in this same
      // payload — pick them up so the bottom panel matches the planner.
      if (Array.isArray(e.detail?.routeLegs)) {
        setMonitoringLegs(e.detail.routeLegs);
      }
    }
    window.addEventListener('highlightOTLeg', onHighlight);
    return () => window.removeEventListener('highlightOTLeg', onHighlight);
  }, []);
  // Track whether we've already auto-collapsed + fit bounds for the current OT monitoring session
  const otMonitoringPreparedRef = React.useRef(false);

  // ─────────────────────────────────────────────────────────────────
  // Memoização do trabalho pesado do painel inferior de OTs.
  //
  // O painel é renderizado dentro dum IIFE gigante na JSX. Sem
  // memoização, cada clique num card disparava re-render do AppLayout
  // → IIFE recomputava do zero:
  //   · cumPoly: haversine sobre TODOS os pontos da polyline
  //     planeada — ~30-50 ms para 13k pontos (rota 400+ km)
  //   · nearestPolyIdx O(N×M) — para 10 OTs × 13k pontos = ~5-10 ms
  //   · construção dos points/segMetrics e formatação
  // Nada disto depende do `activeOTLegId` — só de currentRoute. Aqui
  // calculamos uma vez por carregamento de rota e o IIFE consome.
  const routePanelData = useMemo(() => {
    const cr = currentRoute.computedRoute || {};
    const rawLegs = Array.isArray(cr.legs) ? cr.legs : [];
    const legDist = (l) => Number(l?.distance != null ? l.distance : (l?.summary && l.summary.length) || 0);
    const legDur  = (l) => Number(l?.duration != null ? l.duration : (l?.summary && (l.summary.baseDuration || l.summary.duration)) || 0);
    const totalDistM = Number(cr.distance || 0) || rawLegs.reduce((s, l) => s + legDist(l), 0);
    const totalDurS = Number(cr.duration || 0) || rawLegs.reduce((s, l) => s + legDur(l), 0);

    const polyCoords = Array.isArray(cr.geometry?.coordinates) ? cr.geometry.coordinates : [];
    const cumPoly = [];
    if (polyCoords.length > 1) {
      cumPoly[0] = 0;
      const toRad = d => d * Math.PI / 180;
      const R = 6371;
      for (let k = 1; k < polyCoords.length; k++) {
        const [lng1, lat1] = polyCoords[k - 1];
        const [lng2, lat2] = polyCoords[k];
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        cumPoly[k] = cumPoly[k - 1] + 2 * R * Math.asin(Math.sqrt(a)) * 1000;
      }
    }
    function nearestPolyIdx(coords) {
      if (!Array.isArray(coords) || polyCoords.length === 0) return -1;
      const [lng, lat] = coords;
      let best = -1, bestD = Infinity;
      for (let k = 0; k < polyCoords.length; k++) {
        const dx = polyCoords[k][0] - lng;
        const dy = polyCoords[k][1] - lat;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = k; }
      }
      return best;
    }

    const hasOrigin = !!(currentRoute.origin?.name);
    const hasDestination = !!(currentRoute.destination?.name);
    const waypointsArr = Array.isArray(currentRoute.waypoints)
      ? currentRoute.waypoints.filter(w => w && (w.name || w.coordinates)) : [];
    const points = [];
    if (hasOrigin) {
      const o = currentRoute.origin;
      points.push({
        kind: 'partida', name: o.name,
        orderNumber: o.orderNumber || null, customRef: o.customRef || null,
        otId: o.otId || null, waypointKind: o.waypointKind || null,
        coordinates: o.coordinates || null, otStatus: o.otStatus || null,
      });
    }
    waypointsArr.forEach((w, i) => {
      points.push({
        kind: 'paragem', paragemIdx: i + 1, name: w.name,
        orderNumber: w.orderNumber || null, customRef: w.customRef || null,
        otId: w.otId || null, waypointKind: w.waypointKind || null,
        coordinates: w.coordinates || null, otStatus: w.otStatus || null,
      });
    });
    if (hasDestination) {
      const d = currentRoute.destination;
      // "Regresso a casa/base" — quando o destino não tem otId mas o
      // nome começa com 🏠 (casa do motorista) ou 🏢 (parque/base),
      // injectamos o pseudo-id '__return__'. O MapView já sabe lidar
      // com este id (`otLegPath` aceita-o) e o card fica clicável.
      const dName = typeof d.name === 'string' ? d.name : '';
      const isReturn = !d.otId && (dName.startsWith('🏠') || dName.startsWith('🏢'));
      points.push({
        kind: 'destino', name: d.name,
        orderNumber: d.orderNumber || (isReturn ? (lang === 'pt' ? 'Regresso' : 'Return') : null),
        customRef: d.customRef || null,
        otId: d.otId || (isReturn ? '__return__' : null),
        waypointKind: d.waypointKind || null,
        coordinates: d.coordinates || null, otStatus: d.otStatus || null,
        isReturn,
      });
    }

    const gaps = Math.max(0, points.length - 1);
    const segMetrics = [];
    if (gaps === 1) {
      segMetrics.push({
        km: totalDistM > 0 ? totalDistM / 1000 : null,
        min: totalDurS > 0 ? Math.round(totalDurS / 60) : null,
      });
    } else if (gaps > 0 && rawLegs.length === gaps) {
      for (let i = 0; i < gaps; i++) {
        const dM = legDist(rawLegs[i]);
        const dS = legDur(rawLegs[i]);
        segMetrics.push({
          km: dM > 0 ? dM / 1000 : null,
          min: dS > 0 ? Math.round(dS / 60) : null,
        });
      }
    } else if (gaps > 0 && polyCoords.length > 1) {
      const pointIndices = points.map(p => nearestPolyIdx(p.coordinates));
      const rawSegs = [];
      let sumRaw = 0;
      for (let i = 0; i < gaps; i++) {
        const a = pointIndices[i], b = pointIndices[i + 1];
        const d = (a >= 0 && b >= 0 && b > a) ? Math.max(0, cumPoly[b] - cumPoly[a]) : 0;
        rawSegs.push(d);
        sumRaw += d;
      }
      const scaleKm = (sumRaw > 0 && totalDistM > 0) ? (totalDistM / sumRaw) : 1;
      const scaleMin = (sumRaw > 0 && totalDurS > 0) ? (totalDurS / sumRaw) : 1;
      for (let i = 0; i < gaps; i++) {
        const dM = rawSegs[i] * scaleKm;
        const dS = rawSegs[i] * scaleMin;
        segMetrics.push({
          km: dM > 0 ? dM / 1000 : null,
          min: dS > 0 ? Math.round(dS / 60) : null,
        });
      }
    } else {
      for (let i = 0; i < gaps; i++) segMetrics.push({ km: null, min: null });
    }

    // Contar OTs DISTINTAS, não pontos. Uma OT de combustível ocupa vários
    // pontos (recolha + 1/2 descargas) mas é sempre 1 OT. O regresso a casa
    // (__return__) não é uma OT.
    const otKeys = new Set();
    points.forEach(p => {
      if (p.otId === '__return__') return;
      const key = p.otId != null ? `id:${p.otId}`
        : p.orderNumber ? `num:${p.orderNumber}`
        : p.customRef ? `ref:${p.customRef}` : null;
      if (key) otKeys.add(key);
    });
    const otCount = otKeys.size;
    return { points, segMetrics, hasOrigin, hasDestination, otCount, totalDistM, totalDurS };
  }, [currentRoute.computedRoute, currentRoute.waypoints, currentRoute.origin, currentRoute.destination]);

  // ── Reordenar pontos da rota por drag no painel inferior ──────────────
  // Só para rotas de ASSISTÊNCIA/ENTREGA em edição (gateado por tipo de OT,
  // não por variante: exclui fuel e o modo só-consulta). A PARTIDA e os
  // pontos JÁ ENTREGUES pelo motorista ficam bloqueados. Arrastar um ponto
  // para último promove-o a DESTINO (promove/despromove waypoint↔destino).
  const [dragPointIdx, setDragPointIdx] = useState(null);
  const [dragOverPointIdx, setDragOverPointIdx] = useState(null);
  const handleReorderRoutePoint = (fromCardIdx, toCardIdx) => {
    if (fromCardIdx == null || toCardIdx == null || fromCardIdx === toCardIdx) return;
    const wps = Array.isArray(currentRoute.waypoints)
      ? currentRoute.waypoints.filter(w => w && (w.name || w.coordinates)) : [];
    const hasDest = !!(currentRoute.destination?.name);
    const seq = hasDest ? [...wps, currentRoute.destination] : [...wps];
    const from = fromCardIdx - 1; // card 0 = PARTIDA (fixa, nunca entra na sequência)
    const to = toCardIdx - 1;
    if (from < 0 || to < 0 || from >= seq.length || to >= seq.length) return;
    // Bloqueio dos já entregues: nada se move nem se larga antes do último entregue.
    let lastDelivered = -1;
    wps.forEach((w, i) => { if (w.otStatus === 'delivered') lastDelivered = i; });
    const minMovable = lastDelivered + 1;
    if (from < minMovable || to < minMovable) return;
    const next = [...seq];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    if (hasDest) {
      dispatch(setWaypoints(next.slice(0, next.length - 1)));
      dispatch(setDestination(next[next.length - 1]));
    } else {
      dispatch(setWaypoints(next));
    }
    // Recalcular AUTOMATICAMENTE ao largar — assim a toolbar de topo (Gravar/
    // Despachar), que depende de haver rota calculada, reaparece sozinha e não
    // é preciso ir clicar no botão "Recalcular" (que podia ficar escondido
    // atrás do painel inferior em rotas longas). O RouteForm reage a esta flag.
    dispatch(setNeedsRecalculation(true));
    setDragPointIdx(null);
    setDragOverPointIdx(null);
  };

  // Ao terminar o re-plano (replanningRouteId volta a null por Guardar/Cancelar),
  // limpa a flag dedicada e o __monitoringOT que o re-plano pôs — sem tocar no
  // __monitoringOT do fluxo de monitorização (esse nunca liga __replanning).
  useEffect(() => {
    if (!currentRoute.replanningRouteId && window.__replanning) {
      window.__replanning = false;
      window.__monitoringOT = null;
    }
  }, [currentRoute.replanningRouteId]);

  const [detailsWidth, setDetailsWidth] = useState(380);
  const [resizing, setResizing] = useState(null); // 'sidebar' | 'details' | null
  // Drag position for floating panels
  const [sidebarPos, setSidebarPos] = useState({ x: 12, y: 62 });
  const [detailsPos, setDetailsPos] = useState({ x: null, y: 62 }); // x=null means right-aligned
  const [dragging, setDragging] = useState(null); // 'sidebar' | 'details' | null
  const dragStartRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // When a new route is computed, keep sidebar open but details closed
  useEffect(() => {
    if (showDetails) {
      dispatch(setDetailsPanelOpen(false));
    }
  }, [showDetails, dispatch]);

  // When the OT monitoring panel becomes visible (user came from OT screen),
  // collapse both side panels and fit the map to the full route so the user
  // can see everything without overlap.
  // Inspect-mode activo: tanto a partir do ecrã das OTs (Path A) como
  // a partir do carregamento duma saved route no Planeamento (Path B
  // via SavedRoutesQuickPanel → __inspectingRoute). Ambas as portas
  // populam `__monitoringOT` + `__monitoringRouteLegs` para o painel
  // inferior + cores azuis uniformes ficarem consistentes.
  const otMonitoringActive = activeView === 'planner'
    && !!window.__monitoringOT
    && (!!window.__fromOTScreen || !!window.__inspectingRoute)
    && !!currentRoute.computedRoute;
  useEffect(() => {
    if (otMonitoringActive && !otMonitoringPreparedRef.current) {
      otMonitoringPreparedRef.current = true;
      // Collapse both side panels — the bottom monitoring bar already shows the key info
      setMonitoringBarCollapsed(false); // start expanded
      setSidebarCollapsed(true);
      dispatch(setDetailsPanelOpen(false));
      // Wait a tick for the map to settle, then fit the route bounds
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('fitRouteBounds'));
      }, 250);
    } else if (!otMonitoringActive && otMonitoringPreparedRef.current) {
      // Reset the flag when the user leaves monitoring so next entry triggers it again
      otMonitoringPreparedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otMonitoringActive]);

  // Resize handler for draggable panel borders
  useEffect(() => {
    if (!resizing) return;
    function onMove(e) {
      if (resizing === 'sidebar') {
        const w = Math.max(300, Math.min(600, e.clientX - 12));
        setSidebarWidth(w);
      } else if (resizing === 'details') {
        const w = Math.max(300, Math.min(600, window.innerWidth - e.clientX - 12));
        setDetailsWidth(w);
      }
    }
    function onUp() { setResizing(null); }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing]);

  // Drag handler for movable panels
  useEffect(() => {
    if (!dragging) return;
    function onMove(e) {
      const dx = e.clientX - dragStartRef.current.mx;
      const dy = e.clientY - dragStartRef.current.my;
      const newX = Math.max(0, Math.min(window.innerWidth - 200, dragStartRef.current.px + dx));
      const newY = Math.max(50, Math.min(window.innerHeight - 100, dragStartRef.current.py + dy));
      if (dragging === 'sidebar') {
        setSidebarPos({ x: newX, y: newY });
      } else if (dragging === 'details') {
        setDetailsPos({ x: newX, y: newY });
      }
    }
    function onUp() { setDragging(null); }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  // Reset panel positions when sidebar visibility changes
  useEffect(() => {
    if (sidebarVisible) setSidebarPos({ x: 12, y: 62 });
  }, [sidebarVisible]);

  useEffect(() => {
    if (detailsPanelOpen) setDetailsPos({ x: null, y: 62 });
  }, [detailsPanelOpen]);

  // Smart search — searches vehicles, routes, OTs, coordinates, and addresses
  function handleSearchChange(query) {
    setSearchQuery(query);
    if (!query) setSearchPin(null);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.length < 2) { setSearchResults([]); return; }
    const q = query.toLowerCase().trim();
    const results = [];

    // Search vehicles by plate (show position info if available)
    (vehicles || []).forEach(v => {
      if (v.plate && v.plate.toLowerCase().includes(q)) {
        const speed = v.position?.speed ? Math.round(v.position.speed * 1.852) : 0;
        const status = !v.position ? 'Sem posição' : speed > 0 ? `Em movimento · ${speed} km/h` : 'Parado';
        const driverName = v.currentDriver?.name || '';
        const sub = [v.type, driverName, status].filter(Boolean).join(' · ');
        results.push({ type: 'vehicle', icon: 'truck', label: v.plate, sub, data: v });
      }
    });

    // Search saved routes by name
    (savedRoutes || []).forEach(r => {
      if (r.name && r.name.toLowerCase().includes(q)) {
        results.push({ type: 'route', icon: 'route', label: r.name, sub: r.origin ? `${r.origin} → ${r.destination || ''}` : '', data: r });
      }
    });

    // Search OTs by number
    if (q === 'ot' || q.startsWith('ot-') || q.startsWith('ot ') || q.startsWith('ot.')) {
      results.push({ type: 'ot', icon: 'ot', label: lang === 'pt' ? 'Ordens de Transporte' : 'Transport Orders', sub: lang === 'pt' ? 'Abrir gestão de OTs' : 'Open OT management', data: null });
    }

    // Detect GPS coordinates (e.g. "38.7223, -9.1393" or "38.7223 -9.1393")
    const coordMatch = query.match(/^\s*(-?\d+\.?\d*)[,;\s]+(-?\d+\.?\d*)\s*$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        results.unshift({
          type: 'coords', icon: 'coords',
          label: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          sub: lang === 'pt' ? 'Ir para coordenadas GPS' : 'Go to GPS coordinates',
          data: { lat, lng },
        });
      }
    }

    if (results.length > 0) {
      setSearchResults(results.slice(0, 8));
    }

    // Google Places for worldwide address + establishment search.
    // No country restriction — operators may dispatch to Angola, Cape Verde,
    // Mozambique, Brazil, Africa logistics corridors, etc.
    const hasLocalResults = results.length > 0 && results.some(r => r.type !== 'ot');
    searchTimerRef.current = setTimeout(async () => {
      let googleCount = 0;
      try {
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          // New API: AutocompleteSuggestion — worldwide
          const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            // No includedRegionCodes → worldwide coverage
          });
          if (suggestions?.length > 0) {
            googleCount = suggestions.length;
            setSearchResults(prev => {
              const nonAddr = prev.filter(r => r.type !== 'address');
              return [
                ...nonAddr,
                ...suggestions.slice(0, hasLocalResults ? 3 : 6).map(s => {
                  const p = s.placePrediction;
                  return {
                    type: 'address', icon: 'address',
                    label: p?.mainText?.text || p?.text?.text || '',
                    sub: p?.secondaryText?.text || '',
                    data: { placeId: p?.placeId || '', name: p?.text?.text || '' },
                  };
                }),
              ].slice(0, 8);
            });
          }
        } else if (window.google?.maps?.places?.AutocompleteService) {
          // Legacy fallback — worldwide, no country restriction
          const service = new window.google.maps.places.AutocompleteService();
          const predictions = await new Promise((resolve) => {
            service.getPlacePredictions({ input: query }, (preds, status) => {
              resolve(status === 'OK' && preds ? preds : []);
            });
          });
          if (predictions.length > 0) {
            googleCount = predictions.length;
            setSearchResults(prev => {
              const nonAddr = prev.filter(r => r.type !== 'address');
              return [
                ...nonAddr,
                ...predictions.slice(0, hasLocalResults ? 3 : 6).map(p => ({
                  type: 'address', icon: 'address',
                  label: p.structured_formatting?.main_text || p.description,
                  sub: p.structured_formatting?.secondary_text || '',
                  data: { placeId: p.place_id, name: p.description },
                })),
              ].slice(0, 8);
            });
          }
        }
      } catch { /* ignore */ }

      // Fallback / safety net — run geocodeSearchMulti (Mapbox + HERE + OSM)
      // when Google returns nothing. OSM/HERE have excellent coverage for
      // African/Asian logistic hubs that Google may miss.
      if (googleCount === 0) {
        try {
          const geoResults = await geocodeSearchMulti(query);
          if (geoResults.length > 0) {
            setSearchResults(prev => {
              const nonAddr = prev.filter(r => r.type !== 'address');
              return [
                ...nonAddr,
                ...geoResults.slice(0, hasLocalResults ? 2 : 6).map(g => ({
                  type: 'address', icon: 'address',
                  label: g.name.split(',')[0] || g.name,
                  sub: g.name.split(',').slice(1).join(',').trim() || (g.source === 'osm' ? 'OpenStreetMap' : g.source === 'here' ? 'HERE Maps' : 'Mapbox'),
                  data: g,
                })),
              ].slice(0, 8);
            });
          }
        } catch { /* ignore */ }
      }
    }, 300);
  }

  function handleSearchSelect(result) {
    setSearchQuery('');
    setSearchResults([]);
    setSearchFocused(false);
    if (result.type === 'coords') {
      const { lat, lng } = result.data;
      setSearchPin({ lat, lng, name: result.label });
      window.dispatchEvent(new CustomEvent('mapZoomTo', { detail: { lat, lng, zoom: 16 } }));
    } else if (result.type === 'vehicle') {
      const v = result.data;
      if (v?.position) {
        dispatch(navigateTo('planner'));
        window.dispatchEvent(new CustomEvent('focusVehicle', {
          detail: { id: v.id, lat: v.position.latitude, lng: v.position.longitude },
        }));
      } else {
        dispatch(setActiveView('fleet'));
      }
    } else if (result.type === 'route') {
      dispatch(setSidebarVisible(true));
      setSidebarCollapsed(false);
      dispatch(setSidebarTab(3)); // Gravadas tab
      if (result.data?.id && token) dispatch(fetchRouteDetail({ token, routeId: result.data.id }));
    } else if (result.type === 'ot') {
      dispatch(setActiveView('orders'));
    } else if (result.type === 'address') {
      // Use Google Places to get details, zoom to location, and drop pin
      if (result.data?.placeId && window.google?.maps?.places?.Place) {
        // New API: google.maps.places.Place
        (async () => {
          try {
            const place = new window.google.maps.places.Place({ id: result.data.placeId });
            await place.fetchFields({ fields: ['location', 'viewport'] });
            if (place.location) {
              setSearchPin({ lat: place.location.lat(), lng: place.location.lng(), name: result.label });
              window.dispatchEvent(new CustomEvent('mapZoomTo', {
                detail: {
                  name: result.label,
                  placeId: result.data?.placeId || '',
                  lat: place.location.lat(),
                  lng: place.location.lng(),
                  zoom: place.viewport ? undefined : 15,
                  viewport: place.viewport ? {
                    ne: { lat: place.viewport.getNorthEast().lat(), lng: place.viewport.getNorthEast().lng() },
                    sw: { lat: place.viewport.getSouthWest().lat(), lng: place.viewport.getSouthWest().lng() },
                  } : undefined,
                },
              }));
            }
          } catch {
            // Fallback: use Geocoder
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ placeId: result.data.placeId }, (results, status) => {
              if (status === 'OK' && results?.[0]?.geometry?.location) {
                const loc = results[0].geometry.location;
                const vp = results[0].geometry.viewport;
                setSearchPin({ lat: loc.lat(), lng: loc.lng(), name: result.label });
                window.dispatchEvent(new CustomEvent('mapZoomTo', {
                  detail: {
                    lat: loc.lat(), lng: loc.lng(),
                    zoom: vp ? undefined : 15,
                    viewport: vp ? {
                      ne: { lat: vp.getNorthEast().lat(), lng: vp.getNorthEast().lng() },
                      sw: { lat: vp.getSouthWest().lat(), lng: vp.getSouthWest().lng() },
                    } : undefined,
                  },
                }));
              }
            });
          }
        })();
      } else if (result.data?.coordinates) {
        window.dispatchEvent(new CustomEvent('mapZoomTo', {
          detail: { lat: result.data.coordinates[1], lng: result.data.coordinates[0], zoom: 15 },
        }));
      }
    }
  }

  const isPlanner = activeView === 'planner';
  const sidebarShown = sidebarVisible && !sidebarCollapsed;

  return (
    <Box className={classes.root}>
      {/* Header — always at top */}
      <Header />

      {/* Toast notifications (bottom-right) */}
      <ToastContainer />

      {/* Botão "Ajuda" + manual do operador — variante-aware. O HelpCenter só
          mostra o botão se existir manual para a variante do perfil (hoje: fuel;
          petrotec/outros virão com o seu próprio conteúdo). */}
      <HelpCenter variant={isFuelVariant ? 'fuel' : (clientSettings?.uiVariant || 'generic')} lang={lang} />

      {/* Piloto automático da rota (OT fuel) — tapa o "flash" do planeador
          enquanto calcula+grava e volta à cronologia. */}
      {autoSaveReturn && (
        <Box style={{
          position: 'fixed', inset: 0, zIndex: 4000,
          background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box style={{ background: '#fff', borderRadius: 16, padding: '26px 34px', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', textAlign: 'center', maxWidth: 340 }}>
            <Box style={{ fontSize: 34, marginBottom: 8 }}>🚀</Box>
            <Typography style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
              {lang === 'pt' ? 'A calcular e gravar a rota…' : 'Calculating and saving the route…'}
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
              {lang === 'pt' ? 'Volta à cronologia automaticamente.' : 'Returning to the timeline automatically.'}
            </Typography>
            <Box style={{ marginTop: 14 }}><CircularProgress size={26} style={{ color: '#16a34a' }} /></Box>
          </Box>
        </Box>
      )}

      {/* ── Fullscreen views (below header, above map) ── */}
      {/* Cada ecrã de menu é gateado pelo seu MÓDULO (defesa em profundidade:
          o Header já esconde o botão, isto impede chegar cá por outra via). */}
      {activeView === 'dashboard' && (
        <ModuleGate module="dashboard">
          <Box className={classes.fullView}>
            {isMercadoriaVariant ? <CasaisDashboard lang={lang} /> : isFuelVariant ? <FuelOpsDashboard lang={lang} /> : <DashboardView lang={lang} />}
          </Box>
        </ModuleGate>
      )}
      {activeView === 'fleet' && (
        <ModuleGate module="fleet">
          <Box className={classes.fullView}><FleetView lang={lang} /></Box>
        </ModuleGate>
      )}
      {activeView === 'orders' && (
        <ModuleGate module="orders">
          <Box className={classes.fullView}><OrdersView lang={lang} /></Box>
        </ModuleGate>
      )}
      {activeView === 'vrp' && (
        <Box className={classes.fullView}><VRPView lang={lang} /></Box>
      )}
      {activeView === 'reports' && (
        <ModuleGate module="reports">
          <Box className={classes.fullView}><ReportsView /></Box>
        </ModuleGate>
      )}
      {activeView === 'fuel-ops' && (
        <Box className={classes.fullView}><FuelOpsDashboard lang={lang} /></Box>
      )}
      {activeView === 'maintenance' && (
        <Box className={classes.fullView}><MaintenanceHomeView lang={lang} /></Box>
      )}
      {activeView === 'org_admin' && (
        <ModuleGate module="org_admin">
          <Box className={classes.fullView}><OrgAdminView lang={lang} /></Box>
        </ModuleGate>
      )}
      {activeView === 'obras' && (
        <Box className={classes.fullView}><ObrasView lang={lang} /></Box>
      )}
      {activeView === 'motoristas' && (
        <Box className={classes.fullView}><MotoristasView lang={lang} /></Box>
      )}
      {activeView === 'viaturas' && (
        <Box className={classes.fullView}><ViaturasView lang={lang} /></Box>
      )}
      {activeView === 'tiposTransporte' && (
        <Box className={classes.fullView}><TiposTransporteView lang={lang} /></Box>
      )}
      {activeView === 'finalCustomers' && (
        <Box className={classes.fullView}><FinalCustomersView lang={lang} /></Box>
      )}
      {activeView === 'customerSites' && (
        <Box className={classes.fullView}><CustomerSitesView lang={lang} /></Box>
      )}
      {activeView === 'siteEquipments' && (
        <Box className={classes.fullView}><SiteEquipmentsView lang={lang} /></Box>
      )}
      {activeView === 'slaContracts' && (
        <Box className={classes.fullView}><SLAContractsView lang={lang} /></Box>
      )}
      {activeView === 'stockItems' && (
        <Box className={classes.fullView}><StockItemsView lang={lang} /></Box>
      )}
      {activeView === 'stockByVehicle' && (
        <Box className={classes.fullView}><StockByVehicleView lang={lang} /></Box>
      )}
      {activeView === 'accountingCodes' && (
        <Box className={classes.fullView}><AccountingCodesView lang={lang} /></Box>
      )}
      {activeView === 'holidaysAngola' && (
        <Box className={classes.fullView}><HolidaysAngolaView lang={lang} /></Box>
      )}
      {activeView === 'costsSlaDashboard' && (
        <Box className={classes.fullView}><CostsSlaDashboardView lang={lang} /></Box>
      )}
      {activeView === 'routes' && (
        <Box className={classes.fullView}><RoutesView /></Box>
      )}
      {/* Favorites — always mounted to preserve form state during pick-from-map flow */}
      <Box className={classes.fullView} style={{ display: activeView === 'favorites' ? 'flex' : 'none', flexDirection: 'column' }}>
        <ManageFavoritesDialog
          open={activeView === 'favorites'}
          embedded
          onBack={() => dispatch(setActiveView('planner'))}
          onPickFromMap={(callback) => {
            window.__favPickCallback = callback;
            window.dispatchEvent(new CustomEvent('enterAddFavMode'));
            dispatch(setActiveView('planner'));
          }}
        />
      </Box>

      {activeView === 'manage' && (
        <ModuleGate module="operations">
        <Box className={classes.fullView}>
          <ManageDialog
            open={true}
            fullScreen
            onClose={() => {
              // Return to the previous view if we arrived via navigateTo (e.g. from OTs).
              // Otherwise default to planner.
              if (previousView && previousView !== 'manage') {
                dispatch(setActiveView(previousView));
              } else {
                dispatch(setActiveView('planner'));
              }
            }}
            onShowRoutesOnMap={(routes) => {
              dispatch(navigateTo('planner'));
              dispatch(setSidebarVisible(true));
              dispatch(setShowDetails(true));
              dispatch(setDetailsPanelOpen(true));
              if (routes && routes.length > 0) {
                const routeId = routes[0].dbId || routes[0].id;
                const numId = typeof routeId === 'string' ? parseInt(routeId.replace('api-', '')) : routeId;
                if (numId) dispatch(fetchRouteDetail({ token, routeId: numId }));
              }
            }}
            lang={lang}
          />
        </Box>
        </ModuleGate>
      )}

      {/* Map container — always fullscreen behind everything, always mounted */}
      <Box className={classes.mapContainer}>
        {mapProvider === 'google' ? <MapViewGoogle /> : <MapView />}

        {/* Botão "Voltar a X" contextual passou para dentro da MapToolbar
            (componente Map/MapToolbar.js) como primeiro elemento da fila,
            para ficar na mesma barra flutuante da distância/duração e não
            colidir com os controlos de zoom no canto superior direito. */}

        {/* "Voltar à Biblioteca" button removed — its function is duplicated
            by the "Cancelar" button on the replan action bar (which now
            also respects __fromRoutesLibrary and lands on /routes). */}

        {/* "Back to Fleet" button — visible when coming from drivers screen */}
        {activeView === 'planner' && window.__fromDriversScreen && (
          <Box style={{ position: 'absolute', top: 116, right: 56, zIndex: 600 }}>
            <Button variant="contained"
              onClick={() => {
                window.__fromDriversScreen = false;
                dispatch(setActiveView('fleet'));
              }}
              style={{
                fontSize: '0.78rem', fontWeight: 700, textTransform: 'none',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: '#fff', borderRadius: 12, padding: '8px 24px',
                boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
              }}>
              {lang === 'pt' ? '← Voltar à Frota' : '← Back to Fleet'}
            </Button>
          </Box>
        )}

        {/* "Back to OTs" button — visible when viewing a route from the OT screen
            (but só para visualizações sem ligação à OT — quando há sourceOTId/IDs
            a MapToolbar unificada já tem "Cancelar" que cumpre o mesmo papel). */}
        {activeView === 'planner' && currentRoute.computedRoute
          && !currentRoute.sourceOTId
          && !(Array.isArray(currentRoute.sourceOTIds) && currentRoute.sourceOTIds.length > 0)
          && window.__fromOTScreen && (
          <Box style={{ position: 'absolute', top: 116, right: 56, zIndex: 600 }}>
            <Button variant="contained"
              onClick={() => {
                window.__fromOTScreen = false; window.__inspectingRoute = false; window.__monitoringOT = null; window.dispatchEvent(new CustomEvent('focusSingleVehicle', { detail: { id: null } }));
                setCameFromOT(false);
                dispatch(setActiveView('orders'));
                dispatch(clearRoute());
              }}
              style={{
                fontSize: '0.78rem', fontWeight: 700, textTransform: 'none',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#fff', borderRadius: 12, padding: '8px 24px',
                boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
              }}>
              ← {lang === 'pt' ? 'Voltar às Ordens' : 'Back to Orders'}
            </Button>
          </Box>
        )}

        {/* Botão "Fechar rota" foi MOVIDO para MapToolbar (acompanha
            a toolbar flutuante draggable da rota — "Consulta · Fechar
            rota" lado a lado). Ver MapToolbar.js. */}

        {/* "Reenviar versão actualizada" — moved into MapViewGoogle's left toolbar as a MapBtn */}

        {/* Re-plan action bar — prominent banner across the top of the planner.
            Shown when we're editing an in-progress route. Three actions:
              - Guardar e reenviar : PUT + re-dispatch + status=in_progress
              - Só guardar         : PUT only — leaves dispatch/status untouched
              - Cancelar           : discards local edits, no API call
            All three navigate back to Orders; if we came from the Live panel
            (replanReturnLiveOTId is set) the Orders view reopens it. */}
        {/* Barra de replanning antiga — desactivada. A funcionalidade
            migrou para a MapToolbar unificada (Cancelar / Só Gravar /
            Gravar e Despachar). Mantemos o código abaixo comentado por
            via condicional `false &&` enquanto o ciclo é validado. */}
        {false && activeView === 'planner' && currentRoute.replanningRouteId && currentRoute.computedRoute && (() => {
          const routeId = currentRoute.replanningRouteId;
          // Was the route already in progress when the operator opened
          // the editor? If yes, "Guardar e Reenviar" needs to keep it
          // in_progress (the driver is en route). If no (planned/draft),
          // the route MUST stay planned — promoting to in_progress would
          // make the OTs flip to in_transit before the driver started.
          const wasLive = currentRoute.dbStatus === 'in_progress';
          const buildPutBody = () => ({
            name: currentRoute.name,
            origin: currentRoute.origin,
            destination: currentRoute.destination,
            waypoints: currentRoute.waypoints || [],
            routeData: {
              computedRoute: currentRoute.computedRoute,
              stops: currentRoute.stops,
              alternatives: currentRoute.alternatives,
              selectedAlternative: currentRoute.selectedAlternative,
              roadBreakdown: currentRoute.roadBreakdown,
              routeSteps: currentRoute.routeSteps,
              routeOptions: currentRoute.routeOptions,
            },
            totalDistanceM: currentRoute.computedRoute?.distance,
            totalDurationS: currentRoute.computedRoute?.duration,
          });
          // Where to go back to depends on where the operator came from:
          //   - From Operações > Rotas / library  → back to /routes
          //   - From OT screen                    → back to /orders (default)
          // Without this branching the operator always landed on /orders
          // and lost the rotas-list context they were just looking at.
          const returnView = window.__fromRoutesLibrary ? 'routes' : 'orders';
          const finishAndExit = () => {
            dispatch(fetchSavedRoutes(token));
            window.__fromRoutesLibrary = false;
            dispatch(setActiveView(returnView));
            dispatch(clearRoute());
          };
          return (
          <Box style={{
            position: 'absolute', top: 76, left: '50%', transform: 'translateX(-50%)',
            zIndex: 600,
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 18px',
            background: '#fff',
            border: '1px solid #fcd34d',
            borderRadius: 14,
            boxShadow: '0 8px 28px rgba(15,23,42,0.18), 0 0 0 4px rgba(252,211,77,0.18)',
            animation: 'pulse-amber 2.4s ease-in-out infinite',
          }}>
            <Box style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 999,
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              border: '1px solid #fcd34d',
              fontSize: '0.62rem', fontWeight: 800, color: '#92400e',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              ✏ {wasLive
                ? (lang === 'pt' ? 'A editar rota em curso' : 'Editing live route')
                : (lang === 'pt' ? 'A editar rota planeada' : 'Editing planned route')}
            </Box>
            {/* "Guardar e Reenviar" — escondido no perfil combustível (Move
                Express não tem app de motorista, não há nada para reenviar). */}
            {!isFuelVariant && (
            <Button
              variant="contained"
              disabled={savingAndDispatching}
              onClick={async () => {
                setSavingAndDispatching(true);
                try {
                  const putRes = await fetch(`/planner-api/routes/${routeId}`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildPutBody()),
                  });
                  if (!putRes.ok) throw new Error('Falha a guardar rota');
                  // Re-dispatch to current drivers (creates re_dispatched log + websocket emit)
                  const drvRes = await fetch(`/planner-api/routes/${routeId}/drivers`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (drvRes.ok) {
                    const rds = await drvRes.json();
                    const assignments = (rds || [])
                      .filter(rd => rd.driverId)
                      .map(rd => ({ routeId, driverId: rd.driverId }));
                    if (assignments.length > 0) {
                      await fetch('/planner-api/routes/dispatch', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ assignments }),
                      });
                    }
                  }
                  // Only restore in_progress when the route was already live
                  // before the edit. For planned routes (operator preparing
                  // future work), keep status='planned' — the driver gets the
                  // updated PDF/push, but the OTs stay 'assigned' until the
                  // driver hits "Iniciar rota" on the app.
                  if (wasLive) {
                    await fetch(`/planner-api/routes/${routeId}/status`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: 'in_progress' }),
                    });
                  }
                  dispatch(addToast({
                    type: 'success',
                    message: wasLive
                      ? (lang === 'pt' ? '✅ Rota atualizada e reenviada ao motorista' : '✅ Route updated and resent to driver')
                      : (lang === 'pt' ? '✅ Rota planeada actualizada e reenviada (motorista vê a nova versão)' : '✅ Planned route updated and resent (driver sees new version)'),
                  }));
                  finishAndExit();
                } catch (e) {
                  console.error('Replan save+resend error:', e);
                  dispatch(addToast({ type: 'error', message: (lang === 'pt' ? 'Erro: ' : 'Error: ') + (e?.message || e) }));
                }
                setSavingAndDispatching(false);
              }}
              style={{
                fontSize: '0.78rem', fontWeight: 700, textTransform: 'none',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff', borderRadius: 10, padding: '8px 18px',
                boxShadow: '0 3px 10px rgba(245,158,11,0.35)',
              }}>
              {savingAndDispatching ? (lang === 'pt' ? 'A reenviar...' : 'Resending...') : (lang === 'pt' ? '💾 Guardar e Reenviar' : '💾 Save and Resend')}
            </Button>
            )}
            <Button
              variant="outlined"
              disabled={savingAndDispatching}
              onClick={async () => {
                setSavingAndDispatching(true);
                try {
                  const putRes = await fetch(`/planner-api/routes/${routeId}`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildPutBody()),
                  });
                  if (!putRes.ok) throw new Error('Falha a guardar rota');
                  // No re-dispatch, no status change — driver keeps the version
                  // they already have. Operator chose to apply changes silently.
                  dispatch(addToast({
                    type: 'success',
                    message: lang === 'pt' ? '✅ Rota guardada (motorista mantém versão actual)' : '✅ Route saved (driver keeps current version)',
                  }));
                  finishAndExit();
                } catch (e) {
                  console.error('Replan save-only error:', e);
                  dispatch(addToast({ type: 'error', message: (lang === 'pt' ? 'Erro: ' : 'Error: ') + (e?.message || e) }));
                }
                setSavingAndDispatching(false);
              }}
              style={{
                fontSize: '0.78rem', fontWeight: 700, textTransform: 'none',
                borderRadius: 10, padding: '8px 18px',
                // No perfil fuel é a ÚNICA ação de gravação → destaque (preenchido).
                ...(isFuelVariant
                  ? { background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', borderColor: 'transparent', boxShadow: '0 3px 10px rgba(245,158,11,0.35)' }
                  : { background: '#fff', borderColor: '#3b82f6', color: '#1d4ed8' }),
              }}>
              {lang === 'pt' ? (isFuelVariant ? '💾 Guardar' : '💾 Só Guardar') : (isFuelVariant ? '💾 Save' : '💾 Save Only')}
            </Button>
            <Button
              variant="outlined"
              disabled={savingAndDispatching}
              onClick={() => {
                // Discards local edits only — never touches the saved route.
                // The Orders view useEffect picks up replanReturnLiveOTId and
                // reopens the live panel right after this navigation.
                window.__fromRoutesLibrary = false;
                dispatch(setActiveView(returnView));
                dispatch(clearRoute());
              }}
              style={{
                fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
                borderColor: '#e2e8f0', color: '#64748b',
                borderRadius: 10, padding: '8px 16px', background: '#fff',
              }}>
              {lang === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
            <style>{`
              @keyframes pulse-amber {
                0%, 100% { box-shadow: 0 8px 28px rgba(15,23,42,0.18), 0 0 0 4px rgba(252,211,77,0.18); }
                50% { box-shadow: 0 8px 28px rgba(15,23,42,0.22), 0 0 0 6px rgba(252,211,77,0.32); }
              }
            `}</style>
          </Box>
          );
        })()}


        {/* ═══ OT Monitoring Panel — visible when tracking an OT in transit ═══ */}
        {activeView === 'planner' && window.__monitoringOT && (window.__fromOTScreen || window.__inspectingRoute || window.__replanning) && (currentRoute.computedRoute || window.__replanning) && (() => {
          const ot = window.__monitoringOT;
          const vehicles = require('../../store/slices/vehiclesSlice').selectVehicles(require('../../store').default.getState());
          const vehicle = vehicles.find(v => v.id === ot.vehicleId);
          const speed = vehicle?.position?.speed ? Math.round(vehicle.position.speed * 1.852) : 0;
          const attr = vehicle?.position?.attributes || {};
          const cr = currentRoute.computedRoute;
          const distKm = cr?.distance ? Math.round(cr.distance / 1000) : null;
          const durH = cr?.duration ? Math.floor(cr.duration / 3600) : null;
          const durM = cr?.duration ? Math.round((cr.duration % 3600) / 60) : null;
          const stops = currentRoute.stops || [];
          const actNames = { 0: 'Descanso', 1: 'Disponível', 2: 'Trabalho', 3: 'Condução' };
          const actColors = { 0: '#3b82f6', 1: '#64748b', 2: '#f59e0b', 3: '#22c55e' };
          const fmtMin = (m) => { if (m == null) return null; const h = Math.floor(m / 60); const mm = m % 60; return h > 0 ? `${h}h${String(mm).padStart(2, '0')}` : `${mm}min`; };
          return (<>
            {/* Collapsed tab — a small clickable strip at the bottom when the panel is collapsed */}
            {monitoringBarCollapsed && (
              <Box
                onClick={() => setMonitoringBarCollapsed(false)}
                style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 600,
                  height: 32,
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  borderTop: '2px solid #3b82f6',
                  boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
              >
                <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                  ▲ {lang === 'pt' ? `Monitorização OT · ${ot.orderNumber}` : `OT Monitoring · ${ot.orderNumber}`}
                </Typography>
                <Box style={{
                  padding: '1px 8px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.25)',
                  fontSize: '0.58rem', fontWeight: 700, color: '#fff',
                }}>
                  {ot.vehiclePlate || '—'}
                </Box>
              </Box>
            )}

            {/* Full monitoring panel — hidden when collapsed */}
            {!monitoringBarCollapsed && (
            <Box style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 600,
              background: '#fff', borderTop: '2px solid #3b82f6',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
              height: monitoringBarHeight,
              maxHeight: 'calc(100vh - 100px)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              transition: 'height 0.2s ease',
            }}>
              {/* Drag handle — click and drag up/down to resize */}
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  monitoringBarDragRef.current = { startY: e.clientY, startH: monitoringBarHeight };
                  const onMove = (ev) => {
                    if (!monitoringBarDragRef.current) return;
                    const dy = monitoringBarDragRef.current.startY - ev.clientY;
                    const next = Math.max(80, Math.min(window.innerHeight - 120, monitoringBarDragRef.current.startH + dy));
                    setMonitoringBarHeight(next);
                  };
                  const onUp = () => {
                    monitoringBarDragRef.current = null;
                    window.removeEventListener('mousemove', onMove);
                    window.removeEventListener('mouseup', onUp);
                  };
                  window.addEventListener('mousemove', onMove);
                  window.addEventListener('mouseup', onUp);
                }}
                onDoubleClick={() => setMonitoringBarCollapsed(true)}
                title={lang === 'pt' ? 'Arrasta para redimensionar · duplo-clique para recolher' : 'Drag to resize · double-click to collapse'}
                style={{
                  height: 16, width: '100%', flexShrink: 0,
                  cursor: 'ns-resize', userSelect: 'none',
                  background: 'linear-gradient(180deg, rgba(59,130,246,0.08) 0%, transparent 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderBottom: '1px solid #e0e7ff',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(59,130,246,0.16) 0%, transparent 100%)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(59,130,246,0.08) 0%, transparent 100%)'; }}
              >
                <Box style={{
                  width: 36, height: 4, borderRadius: 2,
                  background: '#3b82f6', opacity: 0.55,
                }} />
              </Box>

              {/* Quick controls */}
              <Box style={{
                position: 'absolute', top: 0, right: 8, height: 16,
                display: 'flex', alignItems: 'center', gap: 2, zIndex: 2,
                pointerEvents: 'none',
              }}>
                {/* Collapse button */}
                <Box
                  onClick={() => setMonitoringBarCollapsed(true)}
                  title={lang === 'pt' ? 'Recolher painel' : 'Collapse panel'}
                  style={{
                    width: 16, height: 14, borderRadius: 4, cursor: 'pointer',
                    pointerEvents: 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#fff', border: '1px solid #cbd5e1',
                    fontSize: 10, color: '#64748b', fontWeight: 700,
                  }}
                >▿</Box>
                <Box
                  onClick={() => setMonitoringBarHeight(h => Math.max(80, h - 80))}
                  title={lang === 'pt' ? 'Diminuir' : 'Shrink'}
                  style={{
                    width: 16, height: 14, borderRadius: 4, cursor: 'pointer',
                    pointerEvents: 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#fff', border: '1px solid #cbd5e1',
                    fontSize: 10, color: '#64748b', fontWeight: 700,
                  }}
                >▾</Box>
                <Box
                  onClick={() => setMonitoringBarHeight(h => Math.min(window.innerHeight - 120, h + 80))}
                  title={lang === 'pt' ? 'Aumentar' : 'Expand'}
                  style={{
                    width: 16, height: 14, borderRadius: 4, cursor: 'pointer',
                    pointerEvents: 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#fff', border: '1px solid #cbd5e1',
                    fontSize: 10, color: '#64748b', fontWeight: 700,
                  }}
                >▴</Box>
              </Box>

              {/* "Pontos da rota" timeline — UNIFIED panel for single-OT and multi-OT.
                  Mirrors the LEFT planner panel exactly: PARTIDA → PARAGENS → DESTINO.
                  Data source: currentRoute.origin / .waypoints / .destination (same as
                  the left panel reads). Per-segment km/min from currentRoute.computedRoute.legs
                  (provider-agnostic: HERE writes summary.length/baseDuration; Google writes
                  distance/duration directly).
                  Click on a card with an OT id dispatches `highlightOTLeg` so the map
                  zooms to that specific segment (multi-OT consolidated workflows). */}
              {(() => {
                // Lê do useMemo lá em cima — todas as contas pesadas
                // (cumPoly, points, segMetrics) já estão cacheadas e
                // não recalculam por clique de card.
                const { points, segMetrics: _segMetrics, hasOrigin, hasDestination, otCount } = routePanelData;
                if (!hasOrigin && !hasDestination) return null;
                if (points.length === 0) return null;

                const fmtDur = (m) => m == null ? null : (m >= 60
                  ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`
                  : `${m} min`);
                const fmtKm = (k) => k == null ? null : `${k.toFixed(1)} km`;
                const shortName = (n) => (n || '—').split(',')[0].trim() || '—';

                // segMetrics já vem memoizado do routePanelData (ver
                // useMemo no topo do componente — strategy A/B/C/D
                // documentadas lá em cima).
                const segMetrics = _segMetrics;

                // ── Reordenação por drag (gateada por TIPO de OT, não variante) ──
                // Ativa só em assistência/entrega (sem pontos fuel), com a rota
                // em edição (não read-only) e no Planeamento. minMovableCard =
                // 1.º card que pode mover-se / receber (depois da PARTIDA e dos
                // pontos já ENTREGUES pelo motorista).
                const isFuelRoute = points.some(p => p.waypointKind === 'fuel');
                // hasOrigin garante que o card 0 é SEMPRE a PARTIDA (base do mapeamento card→waypoint).
                const canReorder = activeView === 'planner' && !currentRoute.readOnly && !isFuelRoute && hasOrigin && points.length > 2;
                let lastDeliveredCard = 0; // índice da PARTIDA
                points.forEach((p, i) => { if (p.kind === 'paragem' && p.otStatus === 'delivered') lastDeliveredCard = i; });
                const minMovableCard = Math.max(1, lastDeliveredCard + 1);

                // For each card: palette derived from kind (partida/paragem/destino),
                // OT badge derived from orderNumber/customRef/waypointKind.
                const paletteFor = (kind, isOT, isActive) => {
                  if (kind === 'partida') {
                    return { dot: '#22c55e', border: isActive ? '#16a34a' : '#bbf7d0', bg: '#f0fdf4', label: '#15803d' };
                  }
                  if (kind === 'destino') {
                    return { dot: '#ef4444', border: isActive ? '#dc2626' : '#fecaca', bg: '#fef2f2', label: '#b91c1c' };
                  }
                  // paragem (intermediate)
                  return { dot: '#f59e0b', border: isActive ? '#f97316' : '#fde68a', bg: '#fffbeb', label: '#9a3412' };
                };

                const subLabelFor = (p) => {
                  // Mostra a metadata da OT exactamente como o painel esquerdo: badge tipo + número OT
                  if (p.orderNumber) {
                    const tipo = p.waypointKind === 'service' ? '🔧 Assistência'
                      : p.waypointKind === 'fuel' ? '⛽ Combustível'
                      : p.waypointKind === 'pickup' ? '↑ Recolha'
                      : p.waypointKind === 'delivery' ? '↓ Entrega'
                      : null;
                    return { ot: p.orderNumber, tipo };
                  }
                  if (p.customRef) return { ot: p.customRef, tipo: null };
                  return null;
                };

                return (
                  <Box style={{
                    padding: '6px 14px 8px', borderBottom: '1px solid #f1f5f9',
                    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                  }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Typography style={{ fontSize: '0.56rem', fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {lang === 'pt' ? 'Pontos da rota' : 'Route stops'}
                      </Typography>
                      <Typography style={{ fontSize: '0.54rem', color: '#64748b', fontWeight: 600 }}>
                        {points.length} {lang === 'pt' ? (points.length === 1 ? 'ponto' : 'pontos') : (points.length === 1 ? 'point' : 'points')}
                      </Typography>
                      {otCount > 0 && (
                        <Typography style={{ fontSize: '0.52rem', color: '#9a3412', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#fed7aa' }}>
                          {otCount} {lang === 'pt' ? (otCount === 1 ? 'OT' : 'OTs') : (otCount === 1 ? 'order' : 'orders')}
                        </Typography>
                      )}
                      <Typography style={{ fontSize: '0.52rem', color: '#94a3b8', marginLeft: 'auto', fontStyle: 'italic' }}>
                        {lang === 'pt' ? 'clica num card OT para destacar no mapa' : 'click an OT card to highlight on map'}
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'stretch', gap: 0, overflowX: 'auto', paddingBottom: 2 }}>
                      {points.map((p, idx) => {
                        const sub = subLabelFor(p);
                        const isOT = !!sub;
                        const isActive = isOT && p.otId && String(p.otId) === String(activeOTLegId);
                        const pal = paletteFor(p.kind, isOT, isActive);
                        const labelHeader = p.kind === 'partida'
                          ? (lang === 'pt' ? 'Partida' : 'Start')
                          : p.kind === 'destino'
                          ? (lang === 'pt' ? 'Destino' : 'End')
                          : (lang === 'pt' ? `Paragem ${p.paragemIdx}` : `Stop ${p.paragemIdx}`);
                        const clickable = !!p.otId;
                        const seg = segMetrics[idx] || { km: null, min: null }; // leg BETWEEN this point and the next
                        const showConnector = idx < points.length - 1;
                        // Pode ser arrastado / receber drop? (PARTIDA e entregues bloqueados)
                        const movable = canReorder && idx >= minMovableCard;
                        const isDragging = dragPointIdx === idx;
                        const isDropTarget = movable && dragOverPointIdx === idx && dragPointIdx !== null && dragPointIdx !== idx;
                        return (
                          <React.Fragment key={`${p.kind}-${idx}`}>
                            <Box
                              draggable={movable}
                              onDragStart={movable ? (e) => {
                                setDragPointIdx(idx);
                                try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
                              } : undefined}
                              onDragOver={canReorder ? (e) => {
                                if (idx < minMovableCard) return; // não se pode largar antes da partida/entregues
                                e.preventDefault();
                                try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
                                if (dragOverPointIdx !== idx) setDragOverPointIdx(idx);
                              } : undefined}
                              onDrop={canReorder ? (e) => {
                                e.preventDefault();
                                if (dragPointIdx !== null && dragPointIdx !== idx) handleReorderRoutePoint(dragPointIdx, idx);
                                setDragPointIdx(null); setDragOverPointIdx(null);
                              } : undefined}
                              onDragEnd={() => { setDragPointIdx(null); setDragOverPointIdx(null); }}
                              onClick={clickable ? () => {
                                // Só enviamos `orderId` — `routeLegs` e
                                // `orderedOTIds` já estão em estado desde a
                                // activação do inspect-mode (loadOTOnMap /
                                // SavedRoutesQuickPanel / RoutesBrowserDrawer).
                                // Antes este clique reconstruía os dois arrays
                                // do zero, forçando setMonitoringLegs +
                                // setMonitoringRouteLegs com referências novas
                                // → AppLayout e MapViewGoogle re-renderizavam
                                // tudo (inclusive os 170 cards de OT) cada
                                // vez. Em rotas de 400+km com muitas OTs era
                                // o passo mais caro do clique.
                                window.dispatchEvent(new CustomEvent('highlightOTLeg', { detail: {
                                  orderId: p.otId,
                                } }));
                              } : undefined}
                              title={movable
                                ? (lang === 'pt' ? 'Arrasta para reordenar · clica para destacar no mapa' : 'Drag to reorder · click to highlight')
                                : clickable
                                ? (lang === 'pt' ? 'Clica para destacar este troço no mapa' : 'Click to highlight on map')
                                : (isOT ? '' : (lang === 'pt' ? 'Ponto sem OT associada' : 'Not an order'))}
                              style={{
                                flex: '0 0 170px', minWidth: 170,
                                padding: '6px 9px', borderRadius: 8,
                                cursor: movable ? 'grab' : (clickable ? 'pointer' : 'default'),
                                background: pal.bg,
                                border: `1.5px solid ${isDropTarget ? '#6366f1' : pal.border}`,
                                boxShadow: isDropTarget ? '0 0 0 2px #6366f1' : (isActive ? `0 1px 4px ${pal.dot}55` : 'none'),
                                opacity: isDragging ? 0.4 : 1,
                              }}
                            >
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                                <Box style={{
                                  width: 10, height: 10, borderRadius: '50%',
                                  background: pal.dot, flexShrink: 0,
                                }} />
                                <Typography style={{ fontSize: '0.5rem', fontWeight: 800, color: pal.label, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  {labelHeader}
                                </Typography>
                                {movable && (
                                  <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1, userSelect: 'none' }} title={lang === 'pt' ? 'Arrastar para reordenar' : 'Drag to reorder'}>⠿</span>
                                )}
                              </Box>
                              <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {shortName(p.name)}
                              </Typography>
                              {sub ? (
                                <Box style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                  <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#9a3412', background: '#fed7aa', padding: '0 4px', borderRadius: 3 }}>
                                    {sub.ot}
                                  </Typography>
                                  {sub.tipo && (
                                    <Typography style={{ fontSize: '0.48rem', fontWeight: 600, color: '#64748b' }}>
                                      {sub.tipo}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography style={{ fontSize: '0.5rem', color: '#94a3b8', fontStyle: 'italic', marginTop: 2 }}>
                                  {lang === 'pt' ? 'sem OT' : 'no order'}
                                </Typography>
                              )}
                            </Box>
                            {showConnector && (
                              <ConnectorArrow
                                km={fmtKm(seg.km)}
                                dur={fmtDur(seg.min)}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}

              {/* Content — horizontal layout with 4 sections */}
              <Box style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              {/* Info da rota + da OT monitorizada. O cabeçalho passa
                  a ser o NOME DA ROTA (não a OT em foco) porque as
                  métricas mostradas (distância/duração/paragens) são
                  do trajecto COMPLETO. A OT em foco é apresentada
                  como sub-bloco logo abaixo com pickup/delivery
                  específicos. */}
              <Box style={{ flex: '0 0 25%', padding: '10px 14px', borderRight: '1px solid #f1f5f9', overflow: 'auto' }}>
                <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: '#1e40af', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentRoute.name || 'Rota'}
                </Typography>
                <Typography style={{ fontSize: '0.52rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  {lang === 'pt' ? 'Resumo do trajecto' : 'Route summary'}
                </Typography>
                {distKm && <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>Distância: <b>{distKm} km</b></Typography>}
                {durH != null && <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>Duração: <b>{durH}h{durM > 0 ? durM + 'min' : ''}</b></Typography>}
                {stops.length > 0 && <Typography style={{ fontSize: '0.55rem', color: '#f59e0b' }}>Paragens: <b>{stops.length}</b></Typography>}
                {ot.deliveryWindowFrom && (
                  <Box style={{ marginTop: 8, padding: '4px 8px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a' }}>
                    <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#f59e0b' }}>{lang === 'pt' ? 'JANELA ENTREGA' : 'DELIVERY WINDOW'}</Typography>
                    <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#92400e' }}>
                      {new Date(ot.deliveryWindowFrom).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                )}
                {/* Percurso REAL do veículo — só em rotas em trânsito/
                    concluídas. Legenda das linhas do mapa + métricas. */}
                {monitoringActualTrail?.trail?.length > 1 && cr && (() => {
                  const a = analyzeTrail(monitoringActualTrail.trail, cr.geometry?.coordinates);
                  const plannedKm = cr.distance ? cr.distance / 1000 : 0;
                  const desvioKm = Math.abs(a.realKm - plannedKm);
                  const fmtDur = (m) => (m == null ? '—'
                    : m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m}min`);
                  return (
                    <Box style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed #e2e8f0' }}>
                      <Typography style={{ fontSize: '0.52rem', fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                        Percurso real
                      </Typography>
                      {[
                        { c: '#2563eb', t: 'Planeado' },
                        { c: '#16a34a', t: 'Real' },
                        { c: '#dc2626', t: 'Desvio' },
                      ].map(l => (
                        <Box key={l.t} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                          <Box style={{ width: 12, height: 3, borderRadius: 2, background: l.c, flexShrink: 0 }} />
                          <Typography style={{ fontSize: '0.5rem', color: '#64748b', fontWeight: 600 }}>{l.t}</Typography>
                        </Box>
                      ))}
                      <Typography style={{ fontSize: '0.55rem', color: '#64748b', marginTop: 3 }}>
                        Dist. real: <b style={{ color: '#16a34a' }}>{a.realKm.toFixed(1)} km</b>
                      </Typography>
                      <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>
                        Desvio: <b>{desvioKm.toFixed(1)} km</b>
                      </Typography>
                      <Typography style={{ fontSize: '0.55rem', color: a.offCount > 0 ? '#dc2626' : '#64748b' }}>
                        Fora da rota: <b>{a.offCount > 0 ? `${a.offCount} · ${a.offKm.toFixed(1)} km` : 'nenhum'}</b>
                      </Typography>
                      {a.durMin != null && (
                        <Typography style={{ fontSize: '0.55rem', color: '#64748b' }}>
                          Duração: <b>{fmtDur(a.durMin)}</b>
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>

              {/* Vehicle + Driver */}
              <Box style={{ flex: '0 0 25%', padding: '10px 14px', borderRight: '1px solid #f1f5f9', overflow: 'auto' }}>
                <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Veículo / Motorista</Typography>
                <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{ot.vehiclePlate || '—'}</Typography>
                {ot.driverName && <Typography style={{ fontSize: '0.62rem', color: '#64748b' }}>👤 {ot.driverName}</Typography>}
                {vehicle?.position && (
                  <Box style={{ marginTop: 6 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Box style={{ width: 8, height: 8, borderRadius: '50%', background: speed > 3 ? '#22c55e' : '#f59e0b' }} />
                      <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: speed > 3 ? '#16a34a' : '#d97706' }}>
                        {speed > 0 ? `${speed} km/h` : 'Parado'}
                      </Typography>
                    </Box>
                    {vehicle.position.address && <Typography style={{ fontSize: '0.5rem', color: '#94a3b8', marginTop: 2 }}>📍 {vehicle.position.address}</Typography>}
                  </Box>
                )}
                {ot.actualStartAt && <Typography style={{ fontSize: '0.5rem', color: '#22c55e', marginTop: 4 }}>Início: {new Date(ot.actualStartAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</Typography>}
              </Box>

              {/* Tachograph — hidden for tenants that don't use a tachograph */}
              {clientSettings?.useTachograph !== false && (
              <Box style={{ flex: '0 0 25%', padding: '10px 14px', borderRight: '1px solid #f1f5f9', overflow: 'auto' }}>
                <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Tacógrafo</Typography>
                {attr.io460 != null ? (
                  <Box>
                    <Box style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 6, background: `${actColors[attr.io460] || '#64748b'}15`, marginBottom: 4 }}>
                      <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: actColors[attr.io460] || '#64748b' }}>{actNames[attr.io460] || '—'}</Typography>
                    </Box>
                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', fontSize: '0.52rem', color: '#475569' }}>
                      {attr.io10544 != null && <span>Condução contínua: <b>{fmtMin(attr.io10544)}</b></span>}
                      {attr.io10542 != null && <span>Condução diária: <b>{fmtMin(attr.io10542)}</b></span>}
                      {attr.io10543 != null && <span>Pausa acumulada: <b>{fmtMin(attr.io10543)}</b></span>}
                      {attr.io10534 != null && <span>Descanso diário: <b>{fmtMin(attr.io10534)}</b></span>}
                      {attr.io10537 != null && <span>Próx. pausa: <b style={{ color: '#f59e0b' }}>{fmtMin(attr.io10537)}</b></span>}
                      {attr.io10535 != null && <span>Descanso semanal: <b>{fmtMin(attr.io10535)}</b></span>}
                    </Box>
                  </Box>
                ) : (
                  <Typography style={{ fontSize: '0.6rem', color: '#cbd5e1', fontStyle: 'italic' }}>Sem dados de tacógrafo</Typography>
                )}
              </Box>
              )}

              {/* Stops / Next rest */}
              <Box style={{ flex: '0 0 25%', padding: '10px 14px', overflow: 'auto' }}>
                <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Paragens planificadas</Typography>
                {stops.length > 0 ? stops.slice(0, 4).map((stop, idx) => (
                  <Box key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <Box style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', border: '2px solid #f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography style={{ fontSize: '0.45rem', fontWeight: 800, color: '#f59e0b' }}>P{idx + 1}</Typography>
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                        {stop.label || (stop.duration >= 60 ? 'Descanso' : 'Pausa')} · {stop.duration >= 60 ? `${Math.floor(stop.duration / 60)}h` : `${stop.duration}min`}
                      </Typography>
                      <Typography style={{ fontSize: '0.48rem', color: '#94a3b8' }}>
                        Km {stop.km} {stop.arrivalTime ? `· ${stop.arrivalTime}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )) : (
                  <Typography style={{ fontSize: '0.6rem', color: '#cbd5e1', fontStyle: 'italic' }}>Sem paragens</Typography>
                )}
                {stops.length > 4 && <Typography style={{ fontSize: '0.5rem', color: '#94a3b8' }}>+{stops.length - 4} mais...</Typography>}
              </Box>
              </Box>
            </Box>
            )}
          </>);
        })()}

        {/* Smart search bar — Google Maps style — visible when sidebar is CLOSED */}
        <ClickAwayListener onClickAway={() => setSearchFocused(false)}>
          <Box
            style={{
              position: 'absolute', top: 62, left: 12, zIndex: 400,
              display: 'flex', flexDirection: 'column',
              width: 440, background: '#fff',
              borderRadius: searchFocused && searchResults.length > 0 ? '8px 8px 0 0' : 8,
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              transition: 'opacity 0.3s, transform 0.3s',
              opacity: sidebarVisible ? 0 : 1,
              pointerEvents: sidebarVisible ? 'none' : 'auto',
              transform: sidebarVisible ? 'translateY(-10px)' : 'translateY(0)',
              overflow: 'visible',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Input row */}
            <Box style={{ display: 'flex', alignItems: 'center', height: 44 }}>
              <InputBase
                placeholder={lang === 'pt' ? 'Pesquisar matrícula, morada, coordenadas...' : 'Search plate, address, coordinates...'}
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                style={{ flex: 1, fontSize: '0.88rem', color: '#202124', padding: '0 12px', fontWeight: 400 }}
              />
              <SearchIcon style={{ fontSize: 20, color: '#4285f4', flexShrink: 0, marginRight: 8, cursor: 'pointer' }} />
              <Tooltip title={lang === 'pt' ? 'Direções' : 'Directions'}>
                <Box
                  onClick={() => { dispatch(clearRoute()); dispatch(setDetailsPanelOpen(false)); dispatch(setResetMapView(true)); dispatch(setSidebarVisible(true)); setSidebarCollapsed(false); setSearchQuery(''); setSearchResults([]); setSearchFocused(false); }}
                  style={{
                    width: 24, height: 24, marginRight: 4, flexShrink: 0, cursor: 'pointer',
                    background: '#1a73e8', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 5, transform: 'rotate(45deg)',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" style={{ transform: 'rotate(-45deg)' }}>
                    <path d="M21.71 11.29l-9-9a.996.996 0 00-1.41 0l-9 9a.996.996 0 000 1.41l9 9c.39.39 1.02.39 1.41 0l9-9a.996.996 0 000-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/>
                  </svg>
                </Box>
              </Tooltip>
            </Box>

            {/* Search results dropdown — Google style */}
            {searchFocused && searchResults.length > 0 && (
              <Box style={{
                borderTop: '1px solid #e8eaed', maxHeight: 320, overflowY: 'auto',
                background: '#fff', borderRadius: '0 0 12px 12px',
              }}>
                {searchResults.map((r, i) => (
                  <Box
                    key={i}
                    onClick={() => handleSearchSelect(r)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                      cursor: 'pointer', transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Icon */}
                    <Box style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: r.type === 'address' ? '#f1f3f4' : r.type === 'vehicle' ? '#e8f0fe' : r.type === 'route' ? '#e6f4ea' : '#fef7e0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {r.icon === 'truck' && <LocalShippingIcon style={{ fontSize: 15, color: '#1a73e8' }} />}
                      {r.icon === 'route' && <MapIcon style={{ fontSize: 15, color: '#1e8e3e' }} />}
                      {r.icon === 'ot' && <AssignmentIcon style={{ fontSize: 15, color: '#e37400' }} />}
                      {r.icon === 'coords' && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#1a73e8">
                          <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
                        </svg>
                      )}
                      {r.icon === 'address' && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#70757a">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      )}
                    </Box>
                    {/* Text */}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 500, color: '#202124', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.label}
                        {r.sub && <span style={{ color: '#70757a', fontWeight: 400 }}> {r.sub}</span>}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </ClickAwayListener>

        {/* Floating sidebar */}
        <Box className={`${classes.floatingSidebar} ${!sidebarShown ? classes.floatingSidebarHidden : ''}`}
          style={{ width: sidebarWidth, left: sidebarPos.x, top: sidebarPos.y, right: 'auto', bottom: 'auto' }}
        >
          {/* Drag handle */}
          <Box
            onMouseDown={(e) => {
              e.preventDefault();
              dragStartRef.current = { mx: e.clientX, my: e.clientY, px: sidebarPos.x, py: sidebarPos.y };
              setDragging('sidebar');
            }}
            style={{
              height: 14, cursor: 'move', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '16px 16px 0 0', background: 'transparent',
              flexShrink: 0, transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Box style={{ width: 28, height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.12)' }} />
          </Box>
          <Box style={{ overflow: 'hidden', borderRadius: '0 0 16px 16px', maxHeight: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Close button — glassmorphic pill, top-right */}
            <Box
              onClick={() => {
                // Limpa também o overlay de rotas em comparação — sem
                // isto, ficavam linhas coloridas penduradas no mapa
                // depois de fechar o painel.
                dispatch(clearRoute());
                dispatch(setSidebarVisible(false));
                dispatch(setDetailsPanelOpen(false));
                dispatch(setShowDetails(false));
                dispatch(setDisplayedSavedRoutes([]));
              }}
              title={lang === 'pt' ? 'Fechar painel' : 'Close panel'}
              style={{
                position: 'absolute', top: 8, right: 10, zIndex: 10,
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 0 0 1px rgba(255,255,255,0.5) inset',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.95)';
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.6)';
                e.currentTarget.style.transform = 'scale(1.08)';
                e.currentTarget.querySelector('svg').style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.85)';
                e.currentTarget.style.borderColor = 'rgba(15,23,42,0.08)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.querySelector('svg').style.color = '#64748b';
              }}
            >
              <CloseIcon style={{ fontSize: 13, color: '#64748b', transition: 'color 0.15s' }} />
            </Box>
            <SidebarPanel />
          </Box>
          {/* Collapse toggle on right border */}
          <Box
            className={classes.sidebarEdgeToggle}
            onClick={() => setSidebarCollapsed(true)}
          >
            <ChevronLeftIcon style={{ fontSize: 12 }} />
          </Box>
          {/* Resize handle on right edge */}
          <Box
            onMouseDown={() => setResizing('sidebar')}
            style={{
              position: 'absolute', top: 0, right: -4, bottom: 0, width: 8,
              cursor: 'col-resize', zIndex: 520,
            }}
          />
        </Box>

        {/* Edge toggle to expand sidebar when collapsed (but still visible) */}
        {sidebarVisible && sidebarCollapsed && (
          <Box
            className={classes.sidebarEdgeToggle}
            style={{ position: 'absolute', top: '50%', left: 0, right: 'auto', borderRadius: '0 8px 8px 0', zIndex: 510 }}
            onClick={() => setSidebarCollapsed(false)}
          >
            <ChevronRightIcon style={{ fontSize: 12 }} />
          </Box>
        )}

        {/* Floating details panel */}
        {showDetails && (
          <>
            <Box className={`${classes.floatingDetails} ${!detailsPanelOpen ? classes.floatingDetailsHidden : ''}`}
              style={{ width: detailsWidth, ...(detailsPos.x != null ? { left: detailsPos.x, right: 'auto', top: detailsPos.y, bottom: 'auto' } : { top: detailsPos.y }) }}
            >
              {/* Drag handle */}
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  const rect = e.currentTarget.parentElement.getBoundingClientRect();
                  dragStartRef.current = { mx: e.clientX, my: e.clientY, px: rect.left, py: rect.top };
                  setDragging('details');
                }}
                style={{
                  height: 14, cursor: 'move', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '16px 16px 0 0', background: 'transparent',
                  flexShrink: 0, transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Box style={{ width: 28, height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.12)' }} />
              </Box>
              <Box style={{ overflow: 'hidden', overflowY: 'auto', borderRadius: '0 0 16px 16px', maxHeight: 'calc(100vh - 100px)', flex: 1 }}>
                <RouteDetails />
              </Box>
              {/* Collapse toggle on left border */}
              <Box
                className={classes.sidebarEdgeToggle}
                style={{ right: 'auto', left: -14, borderRadius: '8px 0 0 8px', borderLeft: '1px solid rgba(0,0,0,0.10)', borderRight: 'none', boxShadow: '-2px 0 8px rgba(0,0,0,0.10)' }}
                onClick={() => dispatch(setDetailsPanelOpen(false))}
              >
                <ChevronRightIcon style={{ fontSize: 12 }} />
              </Box>
              {/* Resize handle on left edge */}
              <Box
                onMouseDown={() => setResizing('details')}
                style={{
                  position: 'absolute', top: 0, left: -4, bottom: 0, width: 8,
                  cursor: 'col-resize', zIndex: 520,
                }}
              />
            </Box>
            {/* Edge toggle to open details when hidden */}
            {!detailsPanelOpen && (
              <Box
                className={classes.sidebarEdgeToggle}
                style={{ position: 'absolute', top: '50%', right: 0, left: 'auto', borderRadius: '8px 0 0 8px', borderLeft: '1px solid rgba(0,0,0,0.10)', borderRight: 'none', boxShadow: '-2px 0 8px rgba(0,0,0,0.10)', zIndex: 510 }}
                onClick={() => dispatch(setDetailsPanelOpen(true))}
              >
                <ChevronLeftIcon style={{ fontSize: 12 }} />
              </Box>
            )}
          </>
        )}
      </Box>

      <ModuleGate module="reports">
        {showReports && <ReportsModal />}
      </ModuleGate>
      {/* mobile_preview deixou de ser módulo: é um botão do planner, não uma
          capacidade vendável. Fica sempre disponível. */}
      {showMobilePreview && <PhonePreview />}

      {/* Routes browser drawer — only in the *neutral* planner state. We
          hide it whenever the operator is in any contextual flow (loading
          an OT, editing/replanning a route, monitoring live, coming from
          drivers/routes library) so the bottom of the screen isn't fighting
          for attention with the back-banner and contextual CTAs. */}
      {activeView === 'planner'
        && !currentRoute.replanningRouteId
        && !currentRoute.sourceOTId
        && !(Array.isArray(currentRoute.sourceOTIds) && currentRoute.sourceOTIds.length > 0)
        && !window.__fromOTScreen
        && !window.__fromRoutesLibrary
        && !window.__fromDriversScreen
        && !window.__monitoringOT
        && !window.__inspectingRoute
        && clientSettings?.routesBrowser !== false
        && <RoutesBrowserDrawer />}
    </Box>
  );
}

export default AppLayout;
